import React, {useMemo} from 'react';
import {toast} from 'react-toastify';
import {z} from 'zod';
import {useForm} from 'react-hook-form';
import {zodResolver} from '@hookform/resolvers/zod';
import {DialogTitle} from '@headlessui/react';
import {useCreateInvitation, useGetProjects, type CreateInvitationBody} from 'api/generated';
import {Button, Divider, DropdownMulti, InputText, Modal} from 'components';

const schema = z.object({
  emails: z.string(),
  projectUids: z.array(z.string()),
});

interface IModalTeamInviteProps {
  visible: boolean;
  onClose: () => void;
}

const ModalTeamInvite = ({visible, onClose}: IModalTeamInviteProps) => {
  const {data: projectsData} = useGetProjects({filter: 'mine'});
  const projects = projectsData?.data?.data ?? [];

  const createInvitation = useCreateInvitation();
  const {
    register,
    watch,
    setValue,
    getValues,
    handleSubmit,
    formState: {errors, isDirty},
  } = useForm({
    resolver: zodResolver(schema),
  });

  const onSubmit = async (data: CreateInvitationBody) => {
    const {data: res} = await createInvitation.mutateAsync({data});
    if (res.data) {
      toast.success(res.data.message);
      onClose();
    } else if (res.errors) {
      toast.error(res.errors[0].detail);
    }
  };

  const handleProjectUids = (uid: string) => {
    const projectUids = getValues('projectUids') ?? [];
    const index = projectUids.findIndex(Uid => Uid === uid);
    if (index != -1) {
      setValue(
        'projectUids',
        projectUids.filter(Uid => Uid !== uid),
      );
    } else {
      setValue('projectUids', [...projectUids, uid]);
    }
  };

  const items = useMemo(() => {
    return projects.map(p => {
      return {id: p.uid, label: p.name, onClick: () => handleProjectUids(p.uid)};
    });
  }, [projects]);

  const selected = items.filter(i => (watch('projectUids') ?? []).includes(i.id));

  return (
    <Modal visible={visible} onClose={onClose} size="large" scrollable={false}>
      <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col h-full">
        <DialogTitle as="h3" className="text-xl font-semibold text-gray-900 dark:text-white">
          Invite people to your project(s)
        </DialogTitle>
        <div className="mt-2 mb-2">
          <p className="text-sm text-gray-500 dark:text-darkText">
            Adding people will allow them to view, edit and add models to project(s) in your workspace. Separate multiple emails with commas.
          </p>
          <p className="text-sm text-primary font-semibold pt-2.5 sm:pt-0">
            Note: Models added by them in your project(s) will contribute to your storage limit.
          </p>
        </div>
        <Divider />
        <div className="w-full flex flex-col-reverse items-end sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="w-full">
            <InputText
              label="Email Addresses (comma separated)"
              placeholder="ex. email1@example.com,email2@example.com"
              {...register('emails')}
              error={errors.emails?.message}
            />
          </div>
          <div className="pt-0 sm:pt-2.5">
            <DropdownMulti title="Projects" items={items} selected={selected} limitHeight />
          </div>
        </div>
        <div className="mt-5 w-full flex items-center justify-between">
          <div className="w-20">
            <Button text="Cancel" fullWidth size="extra-small" variant="secondary" disabled={createInvitation.isPending} onClick={onClose} />
          </div>
          <div className="w-20">
            <Button
              type="submit"
              fullWidth
              text="Invite"
              size="extra-small"
              disabled={!watch('emails') || !watch('projectUids') || watch('projectUids').length === 0 || !isDirty}
              loading={createInvitation.isPending}
            />
          </div>
        </div>
      </form>
    </Modal>
  );
};

export default React.memo(ModalTeamInvite);
